import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../Auth/AuthProvider.js";
import { backend_url } from "shared/config.js";
import { formatTimestamp } from "../../utils/formatTimestamp";
import { message } from "antd";
import CreatePostForm from "./CreatePostForm";
import CommentList from "./Comments/CommentList";
import { ChevronDown, ChevronUp, MessageCircle } from "lucide-react";

const InlineComments = ({ post, creator }) => {
  const { user, accessToken } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);

  useEffect(() => {
    if (isExpanded && post) {
      fetchComments();
    }
  }, [isExpanded, post]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${backend_url}/reply/comments?reply_id=${post.id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch comments");
      }

      const commentsData = await response.json();
      setComments(commentsData);
    } catch (error) {
      console.error("Error fetching comments:", error);
      message.error("Failed to load comments");
    }
    setLoading(false);
  };

  const handleAddComment = async (submissionData) => {
    try {
      const response = await fetch(
        `${backend_url}/create/comment_and_ai_response`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer: " + accessToken,
          },
          body: JSON.stringify(submissionData),
        }
      );

      if (response.ok) {
        const response_json = await response.json();

        setComments((prevComments) => [
          ...response_json.data, // Spread the array of new posts
          ...prevComments,
        ]);

        return true; // Success - allows form to reset
      } else {
        const errorData = await response.json();
        console.error("Error creating reply:", errorData.error);
        return false; // Failure - prevents form reset
      }
    } catch (error) {
      console.error("Error creating reply:", error);
      return false; // Failure - prevents form reset
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const response = await fetch(
        `${backend_url}/reply/comments/${commentId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete comment");
      }

      // Remove the comment from the state immediately
      setComments((prevComments) =>
        prevComments.filter((comment) => comment.id !== commentId)
      );

      message.success("Comment deleted successfully");
    } catch (error) {
      console.error("Error deleting comment:", error);
      message.error("Failed to delete comment");
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setShowCommentForm(false);
    }
  };

  const toggleCommentForm = () => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
    setShowCommentForm(!showCommentForm);
  };

  const handleCommentIconClick = () => {
    // Always expand and show the comment form when comment icon is clicked
    setIsExpanded(true);
    setShowCommentForm(true);
  };

  const commentCount = comments.length;

  return (
    <div className="border-t border-gray-100 bg-gray-50">
      {/* Comments Header */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={toggleExpanded}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            <span className="text-sm font-medium">
              {commentCount > 0
                ? `${commentCount} Comment${commentCount > 1 ? "s" : ""}`
                : "Comments"}
            </span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {user && (
            <button
              onClick={toggleCommentForm}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              Add Comment
            </button>
          )}
        </div>
      </div>

      {/* Comments Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-200 bg-white">
          {/* Comment Form */}
          {showCommentForm && (
            <div className="py-4 border-b border-gray-100">
              <CreatePostForm
                onSubmit={handleAddComment}
                user={user}
                formType={CreatePostForm.FORM_TYPES.COMMENT}
                parentId={post?.id}
                placeholder="Add to the discussion..."
                showAdvanced={true}
                isTopLevel={true}
                onCancel={() => setShowCommentForm(false)}
                className="comment-form"
              />
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-blue-600"></div>
              <span className="ml-2 text-gray-500">Loading comments...</span>
            </div>
          ) : (
            /* Comments List */
            <div className="py-4">
              {comments.length > 0 ? (
                <CommentList
                  setComments={setComments}
                  comments={comments}
                  user={user}
                  handleDeleteComment={handleDeleteComment}
                  handleAddComment={handleAddComment}
                  formatTimestamp={formatTimestamp}
                />
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">
                    No comments yet. Be the first to comment!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InlineComments;
